import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, Loader2 } from "lucide-react";

const BLANK = { name: "", role: "", bio: "", teaching_areas: [], languages: [], photo: "", approved: true, is_leadership: false, order: 100 };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function MemberCard({ m, onSaved, onDeleted }) {
  const [d, setD] = useState({ ...BLANK, ...m });
  const [busy, setBusy] = useState(false);
  const isNew = !m.fid;

  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => {
    if (!d.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { ...d, teaching_areas: Array.isArray(d.teaching_areas) ? d.teaching_areas : String(d.teaching_areas).split(",").map((s) => s.trim()).filter(Boolean), languages: Array.isArray(d.languages) ? d.languages : String(d.languages).split(",").map((s) => s.trim()).filter(Boolean) };
      const res = isNew ? await api.post("/admin/faculty", payload) : await api.put(`/admin/faculty/${m.fid}`, payload);
      toast.success("Saved"); onSaved(res.data, isNew);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (isNew) return onDeleted(null);
    if (!window.confirm("Remove this staff member?")) return;
    await api.delete(`/admin/faculty/${m.fid}`); toast.success("Removed"); onDeleted(m.fid);
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const r = await api.post("/admin/faculty/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("photo", r.data.url); toast.success("Photo uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const areas = Array.isArray(d.teaching_areas) ? d.teaching_areas.join(", ") : d.teaching_areas;
  const langs = Array.isArray(d.languages) ? d.languages.join(", ") : d.languages;

  return (
    <div data-testid={isNew ? "faculty-new-card" : `faculty-card-${m.fid}`} className="border border-white/10 rounded-sm p-5 bg-white/5">
      <div className="flex gap-5">
        <div className="shrink-0">
          <div className="h-24 w-24 rounded-full overflow-hidden bg-heraldic/20 border border-white/10 flex items-center justify-center font-serif text-2xl text-heraldic">
            {d.photo ? <img src={d.photo.startsWith("http") ? d.photo : `${BACKEND}${d.photo}`} alt={d.name} className="h-full w-full object-cover" /> : (d.name || "??").split(" ").filter(Boolean).slice(-2).map((n) => n[0]).join("")}
          </div>
          <label className="mt-2 flex items-center justify-center gap-1 text-xs text-paper/60 hover:text-paper cursor-pointer">
            <Upload className="h-3 w-3" /> Photo
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid={isNew ? "faculty-new-photo" : `faculty-photo-${m.fid}`} />
          </label>
        </div>
        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <input data-testid="faculty-name" value={d.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className="bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm" />
          <input data-testid="faculty-role" value={d.role} onChange={(e) => set("role", e.target.value)} placeholder="Role / title" className="bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm" />
          <input value={areas} onChange={(e) => set("teaching_areas", e.target.value)} placeholder="Teaching areas (comma separated)" className="bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm" />
          <input value={langs} onChange={(e) => set("languages", e.target.value)} placeholder="Languages (comma separated)" className="bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm" />
          <textarea data-testid="faculty-bio" value={d.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Biography" rows={3} className="sm:col-span-2 bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm" />
          <div className="sm:col-span-2 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!d.approved} onChange={(e) => set("approved", e.target.checked)} /> Published</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!d.is_leadership} onChange={(e) => set("is_leadership", e.target.checked)} /> Leadership</label>
              <label className="flex items-center gap-1">Order <input type="number" value={d.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-16 bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1" /></label>
            </div>
            <div className="flex gap-2">
              <button data-testid={isNew ? "faculty-create" : `faculty-save-${m.fid}`} onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 bg-heraldic text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Add staff" : "Save"}</button>
              <button onClick={del} className="inline-flex items-center gap-1.5 border border-white/15 text-paper/70 px-3 py-2 rounded-sm text-sm hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminFaculty() {
  const [members, setMembers] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/faculty").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl" data-testid="admin-faculty-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl">Faculty & Staff</h1>
            <p className="text-paper/50 text-sm mt-1">Edit profiles, photos and biographies. Add new staff. Published members appear on the public Faculty page.</p>
          </div>
          <button data-testid="faculty-add-new" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-heraldic text-white px-4 py-2.5 rounded-sm text-sm"><Plus className="h-4 w-4" /> Add staff</button>
        </div>

        <div className="mt-6 space-y-4">
          {adding && <MemberCard m={{}} onSaved={(nm) => { setAdding(false); load(); }} onDeleted={() => setAdding(false)} />}
          {members === null && <p className="text-paper/40">Loading…</p>}
          {members && members.map((m) => (
            <MemberCard key={m.fid} m={m} onSaved={() => load()} onDeleted={() => load()} />
          ))}
          {members && members.length === 0 && !adding && <p className="text-paper/40">No staff yet. Add your first member.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
